import React, { useEffect, useState } from 'react';
import { safeUrl } from "../utils/safeUrl";
import Sidebar from './components/Sidebar';
import Navbar from './components/Navbar';
import toast, { Toaster } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { FiAward, FiCheck, FiX, FiExternalLink } from 'react-icons/fi';
import { openProfilePopup } from './components/ApplicationShared';
import { useStickyState } from '../hooks/useStickyState';
import { useScrollRestoration } from '../hooks/useScrollRestoration';

const getUserId = (row) => row?.userId ?? row?.user_id ?? row?.userID ?? null;

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const STATUS_FILTERS = [
  { label: 'All',      value: '' },
  { label: 'Pending',  value: 'inprogress' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
];

const Spinner = ({ sm }) => (
  <div className={`border-2 border-current border-t-transparent rounded-full animate-spin ${sm ? 'w-3.5 h-3.5' : 'w-4 h-4'}`} />
);

const StatusBadge = ({ status }) => {
  const s = String(status || '').toLowerCase();
  const cls = s === 'approved'
    ? 'bg-green-50 text-green-700'
    : (s === 'rejected' || s === 'reject')
      ? 'bg-red-50 text-red-600'
      : 'bg-amber-50 text-amber-600';
  const label = s === 'approved' ? 'Approved' : (s === 'rejected' || s === 'reject') ? 'Rejected' : 'Pending';
  return <span className={`inline-block text-xs font-medium px-2.5 py-0.5 rounded-full ${cls}`}>{label}</span>;
};

const MileStone = () => {
  const navigate = useNavigate();
  const [milestones, setMilestones]     = useState([]);
  const [loading, setLoading]           = useState(true);
  const [currentPage, setCurrentPage]   = useStickyState(1, "ms_currentPage");
  const [lastPage, setLastPage]         = useState(1);
  const [actionLoading, setActionLoading] = useState(null);
  const [statusFilter, setStatusFilter] = useStickyState('', "ms_statusFilter");

  const fetchMilestones = async (page = 1, status = '') => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) { toast.error('Not authenticated'); setLoading(false); return; }
      let url = `${baseUrl}/creator-hub/application/milestone-history?page=${page}`;
      if (status) url += `&status=${status}`;
      const res    = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const result = await res.json();
      if (result && result.status) {
        setMilestones(result.data || []);
        setCurrentPage(result.pagination?.currentPage || page);
        setLastPage(result.pagination?.lastPage || 1);
      } else {
        toast.error(result?.message || 'Failed to fetch milestone history');
      }
    } catch {
      toast.error('Network error while fetching milestone history');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMilestones(currentPage, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter]);

  useScrollRestoration("milestone", !loading && milestones.length > 0);

  const handleStatusChange = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const prevPage = () => { if (currentPage > 1) setCurrentPage(currentPage - 1); };
  const nextPage = () => { if (currentPage < lastPage) setCurrentPage(currentPage + 1); };

  const formatDate = (d) => {
    if (!d) return '—';
    const date = new Date(d);
    if (isNaN(date)) return d;
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getTypeIcon = (type, url) => {
    if (!type) return null;
    const typeStr   = String(type).toLowerCase();
    const iconProps = { className: 'w-5 h-5', fill: 'currentColor', viewBox: '0 0 24 24' };
    let icon = null;
    let colorClass = 'text-gray-500';

    switch (typeStr) {
      case 'youtube':
        colorClass = 'text-red-600';
        icon = <svg {...iconProps}><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>;
        break;
      case 'instagram':
        colorClass = 'text-pink-600';
        icon = <svg {...iconProps}><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>;
        break;
      case 'twitter':
      case 'x':
        colorClass = 'text-gray-900';
        icon = <svg {...iconProps}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>;
        break;
      case 'facebook':
        colorClass = 'text-blue-600';
        icon = <svg {...iconProps}><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>;
        break;
      case 'tiktok':
        colorClass = 'text-gray-900';
        icon = <svg {...iconProps}><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>;
        break;
      case 'linkedin':
        colorClass = 'text-blue-700';
        icon = <svg {...iconProps}><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>;
        break;
      case 'website':
      case 'web':
      case 'blog':
        colorClass = 'text-green-600';
        icon = <svg {...iconProps} fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>;
        break;
      default:
        colorClass = 'text-gray-500';
        icon = <svg {...iconProps} fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>;
    }

    if (url) {
      return (
        <a href={safeUrl(url)} target="_blank" rel="noreferrer" className={`${colorClass} hover:opacity-70 transition`} title={`${type} — click to open`}>
          {icon}
        </a>
      );
    }
    return <div className={colorClass} title={type}>{icon}</div>;
  };

  const updateMilestoneStatus = async (milestoneId, status) => {
    setActionLoading(milestoneId);
    try {
      const token = localStorage.getItem('token');
      if (!token) { toast.error('Not authenticated'); setActionLoading(null); return; }
      const res    = await fetch(`${baseUrl}/creator-hub/application/milestone-history/status/${milestoneId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status }),
      });
      const result = await res.json();
      if (res.ok) {
        toast.success(result?.message || `Milestone ${status} successfully`);
        setMilestones(prev => prev.map(item =>
          (item.id === milestoneId || item._id === milestoneId) ? { ...item, status } : item
        ));
        fetchMilestones(currentPage, statusFilter);
      } else {
        toast.error(result?.message || 'Failed to update milestone status');
      }
    } catch {
      toast.error('Network error while updating milestone status');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Toaster position="top-right" />
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8 space-y-6">

          {/* Heading */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
              <FiAward size={20} className="text-amber-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Bonus Claim</h2>
              <p className="text-sm text-gray-400 mt-0.5">View and manage milestone bonus records</p>
            </div>
            {!loading && milestones.length > 0 && (
              <span className="ml-auto text-xs bg-gray-200 text-gray-600 font-semibold px-2.5 py-1 rounded-full">
                {milestones.length} shown
              </span>
            )}
          </div>

          {/* Main card */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">

            {/* Filter pills */}
            <div className="px-5 py-4 border-b border-gray-100 flex flex-wrap gap-2">
              {STATUS_FILTERS.map(f => (
                <button
                  key={f.value}
                  onClick={() => handleStatusChange(f.value)}
                  className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition ${
                    statusFilter === f.value
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-16">
                <div className="w-7 h-7 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : milestones.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                <FiAward size={32} className="text-gray-300 mb-3" />
                <p className="text-sm">No milestone records found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-left text-xs text-gray-400 uppercase tracking-wide">
                      <th className="px-5 py-3">#</th>
                      <th className="px-5 py-3">Creator</th>
                      <th className="px-5 py-3 hidden md:table-cell">Title</th>
                      <th className="px-5 py-3">Platform</th>
                      <th className="px-5 py-3 hidden md:table-cell">Milestone</th>
                      <th className="px-5 py-3 hidden lg:table-cell text-right">Reward PX</th>
                      <th className="px-5 py-3 hidden lg:table-cell text-right">Reward View</th>
                      <th className="px-5 py-3">Status</th>
                      <th className="px-5 py-3 hidden xl:table-cell">Created</th>
                      <th className="px-5 py-3 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {milestones.map((m, idx) => {
                      const id         = m.id || m._id;
                      const isApproved = String(m.status || '').toLowerCase() === 'approved';
                      const isRejected = ['rejected', 'reject'].includes(String(m.status || '').toLowerCase());
                      const isLoading  = actionLoading === id;
                      return (
                        <tr key={id || idx} className="hover:bg-gray-50 transition">
                          <td className="px-5 py-3.5 text-gray-400 text-xs">
                            {(currentPage - 1) * 10 + idx + 1}
                          </td>
                          <td className="px-5 py-3.5">
                            {getUserId(m) ? (
                              <button
                                onClick={() => navigate(`/user-full-details/${getUserId(m)}`, { state: { from: '/milestone' } })}
                                className="text-left group"
                                title="View user details"
                              >
                                <div className="font-medium text-gray-800 group-hover:text-blue-600 group-hover:underline transition">
                                  {m.userEmail || m.user_email || ''}
                                  {/* {m.user_name || m.username || '—'} */}
                                </div>
                                {/* <div className="text-xs text-gray-400 group-hover:text-blue-500 transition">
                                  {m.userEmail || m.user_email || ''}
                                </div> */}
                              </button>
                            ) : (
                              <>
                                <div className="font-medium text-gray-800">
                                  {m.userName || m.user_name || m.username || '—'}
                                </div>
                                <div className="text-xs text-gray-400">
                                  {m.userEmail || m.user_email || ''}
                                </div>
                              </>
                            )}
                          </td>
                          <td className="px-5 py-3.5 hidden md:table-cell max-w-[160px]">
                            <span className="block truncate text-gray-700">{m.title || '—'}</span>
                          </td>
                          <td className="px-5 py-3.5">
                            <div className="flex flex-col gap-2">
                              <div className="flex items-center gap-1.5">
                                {getTypeIcon(m.type, m.url) || <span className="text-gray-300">—</span>}
                                {m.url && (
                                  <a
                                    href={safeUrl(m.url)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-gray-300 hover:text-blue-500 transition"
                                    title="Open link"
                                  >
                                    <FiExternalLink size={11} />
                                  </a>
                                )}
                              </div>
                              {m.url && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); openProfilePopup(m.url); }}
                                  className="text-[11px] font-medium bg-blue-50 text-blue-600 hover:bg-blue-100 px-2 py-1 rounded-md transition flex items-center gap-1 w-max"
                                >
                                  View Content <FiExternalLink size={10} />
                                </button>
                              )}
                            </div>
                          </td>
                          <td className="px-5 py-3.5 hidden md:table-cell">
                            {m.milestone ? (
                              <span className="inline-block text-xs font-medium bg-purple-50 text-purple-600 px-2.5 py-0.5 rounded-full">
                                {m.milestone}
                              </span>
                            ) : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-5 py-3.5 hidden lg:table-cell text-right font-semibold text-gray-800">
                            {m.rewardPx || m.reward_px || '—'}
                          </td>
                          <td className="px-5 py-3.5 hidden lg:table-cell text-right text-gray-600">
                            {m.rewardView || m.reward_view || '—'}
                          </td>
                          <td className="px-5 py-3.5">
                            <StatusBadge status={m.status} />
                          </td>
                          <td className="px-5 py-3.5 hidden xl:table-cell text-xs text-gray-400">
                            {formatDate(m.createdAt || m.created_at)}
                          </td>
                          <td className="px-5 py-3.5">
                            <div className="flex items-center gap-1.5 justify-center">
                              {!isApproved && (
                                <button
                                  onClick={() => updateMilestoneStatus(id, 'approved')}
                                  disabled={isLoading}
                                  title="Approve"
                                  className="p-1.5 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition disabled:opacity-40"
                                >
                                  {isLoading ? <Spinner sm /> : <FiCheck size={14} />}
                                </button>
                              )}
                              {!isRejected && (
                                <button
                                  onClick={() => updateMilestoneStatus(id, 'rejected')}
                                  disabled={isLoading}
                                  title="Reject"
                                  className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition disabled:opacity-40"
                                >
                                  {isLoading ? <Spinner sm /> : <FiX size={14} />}
                                </button>
                              )}
                              {isApproved && isRejected && (
                                <span className="text-xs text-gray-300">—</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {!loading && milestones.length > 0 && (
              <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between">
                <span className="text-xs text-gray-400">Page {currentPage} of {lastPage}</span>
                <div className="flex gap-2">
                  <button
                    onClick={prevPage}
                    disabled={currentPage <= 1}
                    className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    Previous
                  </button>
                  <button
                    onClick={nextPage}
                    disabled={currentPage >= lastPage}
                    className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default MileStone;
